import React, { useRef, useState } from "react";
import { Building2, Check, Download, Eye, FileText, Image as ImageIcon, Trash2, Upload, X } from "lucide-react";
import {
  AppBadge, AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  AppModal, FieldError, FileViewerModal, FormField, IconButton, ProgressBar, StatusBadge, Textarea, cn,
} from "../../ui";
import { checkDocumentFile, documentChecklist } from "./studentProfileRules";
import { isTempCompanyId } from "./WorkHistoryEditor";

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const isImage = (mime = "") => mime.startsWith("image/");

/** One uploaded (or queued) file under a kind: its name, its state, what can be done with it. */
const FileRow = ({ doc, queued, readOnly, lockVerified, onView, onRemove, onVerify, onReject, busy }) => {
  const Icon = isImage(doc.mime || doc.file?.type) ? ImageIcon : FileText;
  const status = queued ? null : (doc.verification?.status || "pending");
  const name = queued ? doc.file.name : doc.fileName;
  const removable = !readOnly && onRemove && !(lockVerified && status === "verified");
  return (
    <li className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-sm tw-bg-surface-raised tw-px-2.5 tw-py-1.5 tw-text-[0.8125rem]">
      <Icon size={16} className="tw-shrink-0 tw-text-content-muted" aria-hidden="true" />
      <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-content" title={name}>{name}</span>
      <span className="tw-text-xs tw-text-content-subtle">{formatSize(queued ? doc.file.size : doc.size)}</span>
      {queued ? (
        <AppBadge variant="secondary" size="sm">Ready to upload</AppBadge>
      ) : (
        <StatusBadge status={status} domain="studentDocument" />
      )}
      <span className="tw-flex tw-items-center tw-gap-0.5">
        {!queued && doc.fileUrl && <IconButton label={`View ${name}`} icon={Eye} size="xs" variant="ghost" onClick={() => onView(doc)} />}
        {!queued && doc.downloadUrl && (
          <IconButton label={`Download ${name}`} icon={Download} size="xs" variant="ghost" as="a" href={doc.downloadUrl} />
        )}
        {!queued && onVerify && status !== "verified" && (
          <IconButton label={`Mark ${name} verified`} icon={Check} size="xs" variant="ghost" onClick={() => onVerify(doc)} disabled={busy} />
        )}
        {!queued && onReject && status !== "rejected" && (
          <IconButton label={`Reject ${name}`} icon={X} size="xs" variant="ghost-destructive" onClick={() => onReject(doc)} disabled={busy} />
        )}
        {removable && (
          <IconButton label={`Remove ${name}`} icon={Trash2} size="xs" variant="ghost-destructive" onClick={() => onRemove(doc)} disabled={busy} />
        )}
      </span>
      {!queued && status === "rejected" && doc.verification?.note && (
        <span className="tw-basis-full tw-text-xs tw-text-danger-fg">Rejected: {doc.verification.note}</span>
      )}
    </li>
  );
};

/** The upload in flight under a row: the file's name and how far it has got. */
const UploadProgress = ({ name, percent }) => (
  <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-1" role="status" aria-live="polite">
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-xs tw-text-content-muted">
      <span className="tw-min-w-0 tw-truncate">Uploading {name}…</span>
      <span className="tw-tabular-nums">{percent}%</span>
    </div>
    <ProgressBar value={percent} color="primary" />
  </div>
);

/**
 * DocumentsPanel — the three sections a student's papers are filed under
 * (personal · education · experience), each kind with its files and an Upload
 * button. The experience section repeats per previous company and only when
 * the student has experience.
 *
 * A chosen file is checked HERE first — a listed type, an image for a photo,
 * within the size limit — and refused under its own row before a byte is
 * sent; the server checks again, by bytes. While a file uploads its row shows
 * the progress, and anything that fails (an upload, a remove, a verify) is
 * said under the row it belongs to.
 *
 * Two modes, one component:
 *   live      — `onUpload(item, { onProgress })` sends the file at once (the
 *               record exists); the caller refreshes `documents`
 *   deferred  — `onQueue(item)` keeps the file until the student is saved
 *               (Add student); `pending` lists what is queued, `onUnqueue(id)`
 *               drops one
 *
 * `onVerify(doc, status, note)` is passed only by a screen whose viewer may
 * verify (contract students). `lockVerified` is for the student's own screen:
 * the server refuses a student removing a verified file, and lets staff.
 * Permissions are decided by the page, never here.
 * Every callback may reject with an Error whose `message` is the sentence to show.
 */
const DocumentsPanel = ({
  sections = [],
  documents = [],
  pending = [],
  experienceMonths = 0,
  workHistory = [],
  accept = ".pdf,.jpg,.jpeg,.png,.webp",
  imageAccept = ".jpg,.jpeg,.png,.webp",
  maxMb = 5,
  readOnly = false,
  lockVerified = false,
  busy = false,
  onUpload,
  onQueue,
  onUnqueue,
  onRemove,
  onVerify,
}) => {
  const inputRef = useRef(null);
  const target = useRef(null);
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");
  // Per row: the sentence that refused or failed the last action, and the
  // upload in flight. Keyed by the checklist row, so each sits under its field.
  const [rowErrors, setRowErrors] = useState({});
  const [uploads, setUploads] = useState({}); // rowKey → { name, percent }

  const checklist = documentChecklist(sections, documents, { experienceMonths, workHistory, pending });
  const rowKeyOf = (doc) => `${doc.kind}:${doc.companyId ? String(doc.companyId._id || doc.companyId) : ""}`;
  const setRowError = (key, message) => setRowErrors((e) => (message ? { ...e, [key]: message } : (() => { const { [key]: _, ...rest } = e; return rest; })()));

  const pick = (row) => {
    target.current = row;
    if (inputRef.current) {
      inputRef.current.accept = row.kind.imageOnly ? imageAccept : accept;
      inputRef.current.click();
    }
  };

  const chosen = async (e) => {
    const file = e.target.files?.[0];
    const row = target.current;
    e.target.value = "";
    if (!file || !row) return;

    const verdict = checkDocumentFile(file, row.kind, { accept, imageAccept, maxMb });
    if (!verdict.ok) {
      setRowError(row.key, verdict.reason);
      return;
    }
    setRowError(row.key, "");

    const item = { kind: row.kind.key, companyId: row.companyId, file };
    if (!onUpload) {
      onQueue?.({ ...item, id: `${row.key}:${Date.now()}` });
      return;
    }
    setUploads((u) => ({ ...u, [row.key]: { name: file.name, percent: 0 } }));
    try {
      await onUpload(item, {
        onProgress: (percent) => setUploads((u) => (u[row.key] ? { ...u, [row.key]: { ...u[row.key], percent } } : u)),
      });
    } catch (err) {
      setRowError(row.key, err?.message || "The document could not be uploaded.");
    } finally {
      setUploads((u) => { const { [row.key]: _, ...rest } = u; return rest; });
    }
  };

  /** A remove or a verify on one file: its failure lands under that file's row. */
  const rowAction = async (doc, fn) => {
    const key = rowKeyOf(doc);
    setRowError(key, "");
    try {
      await fn();
    } catch (err) {
      setRowError(key, err?.message || "Something went wrong. Try again.");
    }
  };

  const confirmReject = async () => {
    if (!rejecting || !note.trim()) return;
    const doc = rejecting;
    const reason = note.trim();
    setRejecting(null);
    setNote("");
    await rowAction(doc, () => onVerify(doc, "rejected", reason));
  };

  if (!checklist.length) return null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* One hidden picker for every Upload button: `accept` is set per kind before it opens. */}
      <input ref={inputRef} type="file" className="tw-hidden" tabIndex={-1} aria-hidden="true" data-testid="documents-file-input" onChange={chosen} />

      {checklist.map(({ section, rows, companies }) => (
        <AppCard key={section.key}>
          <AppCardHeader>
            <AppCardTitle>{section.label}</AppCardTitle>
            <AppCardDescription>{section.description} PDF or image, up to {maxMb} MB each.</AppCardDescription>
          </AppCardHeader>
          <AppCardContent>
            {companies && companies.length === 0 ? (
              <p className="tw-m-0 tw-text-sm tw-text-content-subtle">Add the previous companies above first — each one gets its own documents.</p>
            ) : (
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
                {rows.map((row, i) => {
                  const firstOfCompany = companies && (i === 0 || rows[i - 1].companyId !== row.companyId);
                  // A company added on the edit form has no saved id yet, and a
                  // live upload needs one: the student is saved first.
                  const unsavedCompany = !!onUpload && isTempCompanyId(row.companyId);
                  const uploading = uploads[row.key];
                  const rowError = rowErrors[row.key];
                  return (
                    <React.Fragment key={row.key}>
                      {firstOfCompany && (
                        <li className={cn("tw-flex tw-items-center tw-gap-1.5 tw-text-[0.8125rem] tw-font-semibold tw-text-content", i > 0 && "tw-mt-2")}>
                          <Building2 size={14} className="tw-text-content-muted" aria-hidden="true" /> {row.companyTitle}
                        </li>
                      )}
                      <li
                        role="group"
                        aria-label={row.companyTitle ? `${row.kind.label} — ${row.companyTitle}` : row.kind.label}
                        className={cn("tw-rounded-md tw-border tw-border-solid tw-p-2.5", rowError ? "tw-border-danger" : "tw-border-line")}
                      >
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                          <span className="tw-text-sm tw-text-content">
                            {row.kind.label}
                            {row.kind.optional && <span className="tw-ml-1 tw-text-xs tw-text-content-subtle">(if applicable)</span>}
                          </span>
                          <span className="tw-flex tw-items-center tw-gap-2">
                            {row.files.length === 0 && row.pending.length === 0 && !uploading && (
                              <span className="tw-text-xs tw-text-content-subtle">{unsavedCompany ? "Save the student first" : "Not uploaded"}</span>
                            )}
                            {!readOnly && row.canAdd && !unsavedCompany && !uploading && (onUpload || onQueue) && (
                              <AppButton size="xs" variant="outline" type="button" onClick={() => pick(row)} disabled={busy}>
                                <Upload size={14} /> {row.files.length || row.pending.length ? "Add another" : "Upload"}
                              </AppButton>
                            )}
                          </span>
                        </div>
                        {(row.files.length > 0 || row.pending.length > 0) && (
                          <ul className="tw-m-0 tw-mt-2 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
                            {row.files.map((doc) => (
                              <FileRow
                                key={doc._id}
                                doc={doc}
                                readOnly={readOnly}
                                lockVerified={lockVerified}
                                busy={busy}
                                onView={setViewing}
                                onRemove={onRemove ? (d) => rowAction(d, () => onRemove(d)) : undefined}
                                onVerify={onVerify ? (d) => rowAction(d, () => onVerify(d, "verified", "")) : undefined}
                                onReject={onVerify ? (d) => { setRejecting(d); setNote(""); } : undefined}
                              />
                            ))}
                            {row.pending.map((item) => (
                              <FileRow key={item.id} doc={item} queued readOnly={readOnly} onRemove={onUnqueue ? () => onUnqueue(item.id) : undefined} />
                            ))}
                          </ul>
                        )}
                        {uploading && <UploadProgress name={uploading.name} percent={uploading.percent} />}
                        {rowError && <FieldError className="tw-mt-2">{rowError}</FieldError>}
                      </li>
                    </React.Fragment>
                  );
                })}
              </ul>
            )}
          </AppCardContent>
        </AppCard>
      ))}

      <FileViewerModal
        show={!!viewing}
        onHide={() => setViewing(null)}
        fileUrl={viewing?.fileUrl}
        fileName={viewing?.fileName}
        fileType={viewing?.mime}
        downloadUrl={viewing?.downloadUrl}
      />

      <AppModal
        show={!!rejecting}
        onHide={() => setRejecting(null)}
        title="Reject this document"
        size="sm"
        footer={(
          <>
            <AppButton variant="outline" onClick={() => setRejecting(null)}>Cancel</AppButton>
            <AppButton variant="destructive" onClick={confirmReject} disabled={!note.trim() || busy}>Reject</AppButton>
          </>
        )}
      >
        <FormField label="Why is it rejected?" hint="The student reads this and uploads a replacement." required>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g. The scan is cut off — upload the full page." />
        </FormField>
      </AppModal>
    </div>
  );
};

export default DocumentsPanel;
